import React, { useEffect } from 'react';
import { Form, Skeleton } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faPen } from '@fortawesome/free-solid-svg-icons';
import { useForm, Controller } from 'react-hook-form';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  InfoRow,
  InfoLabel,
  InfoValue,
  FormSection,
  StyledInput,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  ActionIconButton,
} from '../Styles';
import { useTheme } from '../../../context/ThemeContext';
import { validateEmail, validatePhone } from '../constants';
import { CARD_ICON_STYLE, INFO_ICON_STYLE } from '../constants';
import type { PersonalInformationSectionProps, PersonalFormValues } from '../types';

const PersonalInformationSection: React.FC<PersonalInformationSectionProps> = ({
  user,
  editingSection,
  loading,
  profileLoading,
  onEdit,
  onSave,
  onCancel,
}) => {
  const { isDarkMode } = useTheme();

  const { handleSubmit, control, reset } = useForm<PersonalFormValues>({
    defaultValues: { name: '', email: '', phone: '' },
  });

  useEffect(() => {
    if (user) reset({ name: user.name || '', email: user.email || '', phone: user.phone || '' });
  }, [user, reset]);

  const handleCancel = () => { reset(); onCancel(); };

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faUser} style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Personal Information</CardTitleText>
        </CardTitle>
        <ActionIconButton onClick={profileLoading ? undefined : onEdit} iconColor="#2D6CCA" $loading={profileLoading}>
          <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
        </ActionIconButton>
      </CardHeader>

      {editingSection !== 'personal' ? (
        <div>
          <InfoRow>
            <FontAwesomeIcon icon={faUser} style={INFO_ICON_STYLE} />
            <InfoLabel>Full Name:</InfoLabel>
            <InfoValue>
              {profileLoading ? <Skeleton.Input active size="small" style={{ width: 150, height: 20 }} /> : user?.name || 'Not provided'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <FontAwesomeIcon icon={faEnvelope} style={INFO_ICON_STYLE} />
            <InfoLabel>Email Address:</InfoLabel>
            <InfoValue>
              {profileLoading ? <Skeleton.Input active size="small" style={{ width: 150, height: 20 }} /> : user?.email || 'Not provided'}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <FontAwesomeIcon icon={faPhone} style={INFO_ICON_STYLE} />
            <InfoLabel>Personal Phone Number:</InfoLabel>
            <InfoValue>
              {profileLoading ? <Skeleton.Input active size="small" style={{ width: 150, height: 20 }} /> : user?.phone || 'Not provided'}
            </InfoValue>
          </InfoRow>
        </div>
      ) : (
        <Form layout="vertical" onFinish={handleSubmit(onSave)}>
          <FormSection>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Full name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
              }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Full Name"
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <StyledInput {...field} placeholder="Enter your full name" />
                </Form.Item>
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                validate: (v) => validateEmail(v) || 'Please enter a valid email address',
              }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Email Address"
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <StyledInput {...field} type="email" placeholder="Enter your email" />
                </Form.Item>
              )}
            />
            <Controller
              name="phone"
              control={control}
              rules={{
                required: 'Phone number is required',
                validate: (v) => validatePhone(v) || 'Please enter a valid phone number',
              }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Personal Phone Number"
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <StyledInput {...field} placeholder="Enter your personal phone number" />
                </Form.Item>
              )}
            />
            <ActionButtons>
              <SaveChangesButton icon={<CheckOutlined />} htmlType="submit" loading={loading}>
                Save Changes
              </SaveChangesButton>
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            </ActionButtons>
          </FormSection>
        </Form>
      )}
    </SettingsCard>
  );
};

export default React.memo(PersonalInformationSection);
